import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { profileApi } from '../utils/api';
import Tooltip from '../components/Tooltip';
import type { OnboardingAnswers, GeneratedProfile } from '../types';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<GeneratedProfile | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingAnswers>();

  const onSubmit = async (data: OnboardingAnswers) => {
    setIsGenerating(true);
    try {
      const response = await profileApi.generateProfile(data);
      setGeneratedProfile(response.data);
      toast.success('Profile generated successfully! Please review and approve.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to generate profile');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await profileApi.approveProfile();
      toast.success('Profile approved! You can now receive contract matches.');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error('Failed to approve profile');
    } finally {
      setIsApproving(false);
    }
  };

  const certificationOptions = [
    '8(a)',
    'WOSB (Women-Owned Small Business)',
    'EDWOSB (Economically Disadvantaged WOSB)',
    'SDVOSB (Service-Disabled Veteran-Owned)',
    'HUBZone',
    'SDB (Small Disadvantaged Business)',
  ];

  if (generatedProfile) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Review your contract-matching profile
            </h2>

            {/* Explanation Banner */}
            <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-gray-800 mb-4">
                We used your answers to create a profile that helps us find government contract opportunities that actually fit your business.
              </p>
              <p className="text-gray-800 mb-4">
                This profile is <strong>not a bid</strong> and <strong>not public</strong>. It's only used to filter and rank opportunities by industry, contract size, and what you offer—so you don't have to search SAM.gov yourself.
              </p>
              <ul className="list-disc list-inside text-gray-800 space-y-1 mb-4">
                <li>You don't need to be technical or experienced with government contracts.</li>
                <li>You can edit anything before approving.</li>
                <li>Approving starts your first match run and enables opportunity emails.</li>
                <li>Clear and specific inputs lead to better matches.</li>
              </ul>
              <p className="text-sm text-gray-600">
                We use this information only to generate matches and emails—never to submit bids for you.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900">Primary NAICS Codes</h3>
                  <Tooltip
                    content={
                      <div>
                        <p>These are the main official industry codes used by the government.</p>
                        <p className="mt-2">1–3 strong codes usually perform best.</p>
                        <p className="mt-2">You don't need to cover everything—focus on your core work.</p>
                      </div>
                    }
                    className="ml-2"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {generatedProfile.primary_naics.map((code) => (
                    <span
                      key={code}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900">Related NAICS Codes</h3>
                  <Tooltip
                    content={
                      <div>
                        <p>These help catch closely related opportunities.</p>
                        <p className="mt-2">Remove anything that doesn't reflect what you actually offer.</p>
                      </div>
                    }
                    className="ml-2"
                  />
                </div>
                <div className="mt-2 space-y-2">
                  {generatedProfile.related_naics.slice(0, 5).map((item, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded">
                      <span className="font-medium">{item.code}</span>
                      <span className="text-gray-600 ml-2">{item.explanation}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900">Keywords</h3>
                  <Tooltip
                    content={
                      <div>
                        <p>These phrases help us rank opportunities.</p>
                        <p className="mt-2">Examples:</p>
                        <ul className="mt-1 text-sm list-disc list-inside">
                          <li>'janitorial services', 'facility maintenance'</li>
                          <li>'electrical installation', 'building renovation'</li>
                          <li>'cloud migration', 'data analytics'</li>
                        </ul>
                        <p className="mt-2">Remove anything you don't want work for.</p>
                      </div>
                    }
                    className="ml-2"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {generatedProfile.keywords.slice(0, 15).map((keyword) => (
                    <span
                      key={keyword}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {generatedProfile.inferred_capabilities && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Inferred Capabilities</h3>
                  <ul className="mt-2 list-disc list-inside text-gray-700">
                    {generatedProfile.inferred_capabilities.map((capability, index) => (
                      <li key={index}>{capability}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-8 flex space-x-4">
              <div className="flex-1">
                <div className="flex items-center justify-center mb-2">
                  <Tooltip
                    content="Approving starts matching immediately and turns on your opportunity emails. You can edit this later at any time."
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Ready to approve?</span>
                </div>
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isApproving ? 'Approving...' : 'Approve Profile'}
                </button>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-center mb-2">
                  <Tooltip
                    content="Want better matches? Editing your answers is the fastest way to improve results."
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Need changes?</span>
                </div>
                <button
                  onClick={() => setGeneratedProfile(null)}
                  className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Edit Answers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Tell Us About Your Business
          </h2>
          <p className="text-gray-600 mb-6">
            Answer a few questions so we can match you with relevant government contract opportunities.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Describe your company (1–2 sentences) *
                </label>
                <Tooltip
                  content={
                    <div>
                      <p>Describe what you do and who you serve in plain language.</p>
                      <p className="mt-2">Examples:</p>
                      <ul className="mt-1 text-sm list-disc list-inside">
                        <li>'Commercial cleaning and janitorial services for offices and schools'</li>
                        <li>'General contractor specializing in renovations and small public buildings'</li>
                        <li>'Custom software and cloud infrastructure for small organizations'</li>
                      </ul>
                    </div>
                  }
                  className="ml-2"
                />
              </div>
              <textarea
                {...register('company_description', { required: 'Company description is required' })}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="What does your company do? What's your main focus?"
              />
              {errors.company_description && (
                <p className="mt-1 text-sm text-red-600">{errors.company_description.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Services or products *
                </label>
                <Tooltip
                  content={
                    <div>
                      <p>List the specific services or products you sell—think like a buyer.</p>
                      <p className="mt-2">Examples:</p>
                      <ul className="mt-1 text-sm list-disc list-inside">
                        <li>'Office cleaning, floor waxing, post-construction cleanup'</li>
                        <li>'Concrete work, site prep, ADA upgrades'</li>
                        <li>'IT support, cloud migration, cybersecurity assessments'</li>
                      </ul>
                    </div>
                  }
                  className="ml-2"
                />
              </div>
              <textarea
                {...register('services_products', { required: 'Services/products description is required' })}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="List your main services, products, or capabilities"
              />
              {errors.services_products && (
                <p className="mt-1 text-sm text-red-600">{errors.services_products.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Recent government contracts (optional)
                </label>
                <Tooltip
                  content="If you've done government work, list 1–2 examples. If not, leave this blank—many first-time contractors still get matches."
                  className="ml-2"
                />
              </div>
              <textarea
                {...register('recent_contracts')}
                rows={2}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe any recent government contracts to help us understand your track record"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Minimum contract value ($)
                  </label>
                  <Tooltip
                    content="What's the smallest job worth your time? Example: If you wouldn't pursue a $5,000 job, set this higher."
                    className="ml-2"
                  />
                </div>
                <input
                  {...register('desired_contract_min', { valueAsNumber: true })}
                  type="number"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50000"
                />
              </div>
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Maximum contract value ($)
                  </label>
                  <Tooltip
                    content="What's the largest job you can realistically deliver today? Tip: Setting this too high can surface contracts that are a poor fit."
                    className="ml-2"
                  />
                </div>
                <input
                  {...register('desired_contract_max', { valueAsNumber: true })}
                  type="number"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1000000"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Certifications
                </label>
                <Tooltip
                  content="Select certifications you currently hold. These are often required for set-aside contracts (for example: 8(a), WOSB, SDVOSB)."
                  className="ml-2"
                />
              </div>
              <div className="space-y-2">
                {certificationOptions.map((cert) => (
                  <label key={cert} className="flex items-center">
                    <input
                      type="checkbox"
                      value={cert}
                      {...register('certifications')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{cert}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isGenerating}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isGenerating ? 'Generating Profile...' : 'Generate My Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;